import { Box, Typography } from "@mui/material";
import { ResponsivePie } from "@nivo/pie";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { calcProPercentsBasedOnFilterAndDays, itemPercentage, percentageLabel } from "../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import CardItemTooltip from "../../../../components/shared/CardItemTooltip";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import NoRecordFound from "../../../../components/shared/NoRecordFound";
import CheckboxButton from "../../../../components/dashboard/shared/checkboxButton/CheckboxButton";
import styles from "./ReturnCardList.module.scss";
import ReturnCardListSkeleton from "./ReturnCardListSkeleton";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import CheckboxLoader from "../../../../components/shared/checkbox-loader/CheckboxLoader";

// Constants
const CHART_COLORS = {
	RETURNED: "#2259C9",
	DIDNT_RETURN: "#00BAEB"
};

const CHART_ITEMS = {
	RETURNED: "Returned",
	DIDNT_RETURN: "Didn't Return"
};

const CHART_HEIGHT = "276px";
/** Header filter popover: smaller pie footprint */
const CHART_HEIGHT_HEADER = "168px";
const CHART_MARGIN = { top: 8, right: 8, bottom: 8, left: 8 };
const CHART_MARGIN_HEADER = { top: 2, right: 2, bottom: 2, left: 2 };

const ReturnCardList = ({
	data,
	comparisonData,
	transfers,
	transfersComparison,
	handleToggle,
	selectedItem = [],
	filter,
	filterComparison,
	type,
	page,
	cardTitle,
	loading,
	projectionDays,
	priorityNumber,
	transferType,
	isComparingAgainstAvgCensus,
	lockedTotalBy,
	isCustom = false,
	question,
	isCustomTab = false,
	customTab = null,
	disableInteractions = false,
	compactForHeaderPopover = false,
}) => {
	const [chartData, setChartData] = useState([]);
	const [returnedComparisonDiffColor, setReturnedComparisonDiffColor] = useState("");
	const [didntReturnComparisonDiffColor, setDidntReturnComparisonDiffColor] = useState("");
	const [ecItemComparison, setEcItemComparison] = useState(null);
	const [dcItemComparison, setDcItemComparison] = useState(null);
	const [numberOfDaysReturned, setNumberOfDaysReturned] = useState(null);
	const [numberOfDaysDidntReturn, setNumberOfDaysDidntReturn] = useState(null);
	const [returnedComparisonPercentage, setReturnedComparisonPercentage] = useState(null);
	const [didntReturnComparisonPercentage, setDidntReturnComparisonPercentage] = useState(null);

	const percentageThresholds = useSelector((state) => state.comparison.percentageThresholds);
	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();

	// Computed values
	const isFirstItemInPriorityData = priorityNumber === 1;
	const shouldUseCensusCalculation = useMemo(() =>
		isComparingAgainstAvgCensus ||
		lockedTotalBy === "census" ||
		(isFirstItemInPriorityData && (!transferType || transferType.length === 0)),
		[isComparingAgainstAvgCensus, lockedTotalBy, isFirstItemInPriorityData, transferType]
	);

	// Helper functions
	const createChartItem = useCallback((item, itemType) => {
		const calculatedTotal = calcProPercentsBasedOnFilterAndDays(item?.total || 0, filter, projectionDays);
		const calculatedPercentage = calcProPercentsBasedOnFilterAndDays(
			item?.percentage || itemPercentage(item?.total, null, "number") || 0,
			filter,
			projectionDays,
			true,
			shouldUseCensusCalculation
		);

		return {
			id: itemType,
			label: itemType,
			value: calculatedTotal,
			total: calculatedTotal,
			_id: itemType,
			percentage: calculatedPercentage,
			...item,
		};
	}, [filter, projectionDays, shouldUseCensusCalculation]);

	const processComparisonData = useCallback((dcItem, ecItem, dcItemComparison, ecItemComparison) => {
		if (!comparisonData) return;

		const percentageReturn = itemPercentage(dcItem?.total, transfers, "number");
		const percentageDidntReturn = itemPercentage(ecItem?.total, transfers, "number");
		const percentageReturnComparison = itemPercentage(dcItemComparison?.total, transfersComparison, "number");
		const percentageDidntReturnComparison = itemPercentage(ecItemComparison?.total, transfersComparison, "number");

		// Calculate differences for "Didn't Return"
		const didntReturnDiff = calculateDiffBetweenPercentages(
			percentageDidntReturn,
			percentageDidntReturnComparison,
			false,
			filter,
			filterComparison,
			ecItemComparison?.total,
			projectionDays
		);

		// Calculate differences for "Returned"
		const returnedDiff = calculateDiffBetweenPercentages(
			percentageReturn,
			percentageReturnComparison,
			false,
			filter,
			filterComparison,
			dcItemComparison?.total,
			projectionDays
		);

		// Set comparison colors and data
		setNumberOfDaysReturned(returnedDiff.numberOfDays);
		setNumberOfDaysDidntReturn(didntReturnDiff.numberOfDays);
		setReturnedComparisonPercentage(percentageReturnComparison);
		setDidntReturnComparisonPercentage(percentageDidntReturnComparison);
		setReturnedComparisonDiffColor(pickComparisonColor(returnedDiff.percentageDiff));
		setDidntReturnComparisonDiffColor(pickComparisonColor(didntReturnDiff.percentageDiff));
	}, [comparisonData, transfers, transfersComparison, filter, filterComparison, projectionDays]);

	const handleToggleModified = useCallback((item, type, itemComparison, isChecked) => {
		if (disableInteractions) return;

		handleToggle({
			item,
			type,
			itemComparison,
			setSpecialNumberFlag: false,
			isChecked,
			cardTitle,
			question: isCustom
				? {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab,
					cardTitle,
					customTab
				}
				: null
		});
	}, [disableInteractions, handleToggle, cardTitle, isCustom, question, isCustomTab, customTab]);

	const handleItemClick = useCallback((item, selected) => {
		if (disableInteractions) return;
		handleToggleModified(item, type, undefined, selected);
	}, [disableInteractions, handleToggleModified, type]);

	const handleCheckboxToggle = useCallback((item, selected) => {
		if (disableInteractions) return;
		handleToggleWithLoader(() => handleToggleModified(item, type, undefined, selected), item._id);
	}, [disableInteractions, handleToggleWithLoader, handleToggleModified, type]);

	// Chart data processing
	useEffect(() => {
		if (data && data.length > 0) {
			const dcItem = _.find(data, { _id: CHART_ITEMS.RETURNED });
			const ecItem = _.find(data, { _id: CHART_ITEMS.DIDNT_RETURN });
			const dcItemComparison = _.find(comparisonData, { _id: CHART_ITEMS.RETURNED });
			const ecItemComparison = _.find(comparisonData, { _id: CHART_ITEMS.DIDNT_RETURN });

			setEcItemComparison(ecItemComparison);
			setDcItemComparison(dcItemComparison);

			const chartDetails = [
				createChartItem(dcItem, CHART_ITEMS.RETURNED),
				createChartItem(ecItem, CHART_ITEMS.DIDNT_RETURN)
			];

			processComparisonData(dcItem, ecItem, dcItemComparison, ecItemComparison);
			setChartData(chartDetails);
		} else {
			const emptyChartDetails = [
				{
					id: CHART_ITEMS.RETURNED,
					value: 0,
					total: 0,
					_id: CHART_ITEMS.RETURNED,
					percentage: 0,
				},
				{
					id: CHART_ITEMS.DIDNT_RETURN,
					value: 0,
					total: 0,
					_id: CHART_ITEMS.DIDNT_RETURN,
					percentage: 0,
				},
			];
			setChartData(emptyChartDetails);
		}
	}, [
		data,
		comparisonData,
		transfers,
		transfersComparison,
		page,
		percentageThresholds,
		projectionDays,
		filter,
		filterComparison,
		transferType,
		isComparingAgainstAvgCensus,
		lockedTotalBy,
		isFirstItemInPriorityData,
		createChartItem,
		processComparisonData
	]);

	// Memoized values
	const sortedChartData = useMemo(() =>
		_.orderBy(chartData, "label", "desc"),
		[chartData]
	);

	const chartConfig = useMemo(() => ({
		colors: [CHART_COLORS.RETURNED, CHART_COLORS.DIDNT_RETURN],
		data: chartData,
		margin: compactForHeaderPopover ? CHART_MARGIN_HEADER : CHART_MARGIN,
		padAngle: 0,
		cornerRadius: 0,
		activeOuterRadiusOffset: compactForHeaderPopover ? 3 : 5,
		enableArcLinkLabels: false,
		innerRadius: 0,
		arcLabelsTextColor: "#fff",
		arcLabel: (e) => `${e.value} (${e.data?.percentage}%)`,
		...(compactForHeaderPopover && {
			theme: {
				labels: {
					text: {
						fontSize: 10,
						fontWeight: 600,
					},
				},
			},
		}),
	}), [chartData, compactForHeaderPopover]);

	const renderChartItem = useCallback((item) => {
		const ID = item._id;
		const labelId = `checkbox-list-return-label-${ID}`;
		const selected = selectedItem.indexOf(ID) !== -1;
		const isDidntReturn = ID === CHART_ITEMS.DIDNT_RETURN;

		const colorBoxColor = isDidntReturn ? didntReturnComparisonDiffColor : returnedComparisonDiffColor;
		const comparingAgainst = isDidntReturn ? ecItemComparison?.total : dcItemComparison?.total;

		const comparingAgainstScaled = isDidntReturn ? didntReturnComparisonPercentage : returnedComparisonPercentage;

		const numberOfDays = isDidntReturn ? numberOfDaysDidntReturn : numberOfDaysReturned;
		const labelCircleColor = isDidntReturn ? CHART_COLORS.DIDNT_RETURN : CHART_COLORS.RETURNED;

		return (
			<div
				key={ID}
				className={`df ${styles.sec} cp ${selected ? styles.selected : ""}`}
				onClick={() => handleItemClick(item, selected)}
			>
				<Box position="relative" display="inline-flex" alignItems="center">
					<CheckboxButton
						labelId={labelId}
						handleToggle={() => handleCheckboxToggle(item, selected)}
						checked={selected}
						disabled={disableInteractions}
						colorBox={
							!loading && (
								<ColorBox
									color={colorBoxColor}
									type="list"
									sx={{ margin: "5px 2px 0px 2px" }}
									comparingAgainst={comparingAgainst}
									comparingAgainstScaled={comparingAgainstScaled}
									numberOfDays={numberOfDays}
									customLabel="%"
									hideArrow={true}
									isPercentage={true}
								/>
							)
						}
						buttonText={
							<CardItemTooltip item={item}>
								<Typography
									className={styles.secButtonText}
									variant="subtitle2"
									sx={{
										display: "flex",
										mt: "2px",
										"@media (max-width: 400px)": {
											fontSize: "10px"
										}
									}}
									component="div"
								>
									<div
										className={styles.labelCircle}
										style={{ backgroundColor: labelCircleColor }}
									/>
									{`${item._id}: ${calcProPercentsBasedOnFilterAndDays(
										item.total,
										filter,
										projectionDays
									)} ${percentageLabel(
										item.percentage
											? calcProPercentsBasedOnFilterAndDays(
												item.percentage,
												filter,
												projectionDays,
												true,
												shouldUseCensusCalculation
											)
											: calcProPercentsBasedOnFilterAndDays(
												itemPercentage(item.total, null, "number", page),
												filter,
												projectionDays,
												true,
												shouldUseCensusCalculation
											)
									)}`}
								</Typography>
							</CardItemTooltip>
						}
						disableRipple={true}
						sx={{
							width: "20px",
							...(loadingItems[ID] && { opacity: 0 })
						}}
						textWrapColorClass={item.textWrapColor}
					/>
					<CheckboxLoader isLoading={loadingItems[ID]} />
				</Box>
			</div>
		);
	}, [
		selectedItem,
		didntReturnComparisonDiffColor,
		returnedComparisonDiffColor,
		ecItemComparison,
		dcItemComparison,
		numberOfDaysDidntReturn,
		numberOfDaysReturned,
		loading,
		disableInteractions,
		loadingItems,
		handleItemClick,
		handleCheckboxToggle,
		filter,
		projectionDays,
		shouldUseCensusCalculation,
		page,
		didntReturnComparisonPercentage,
		returnedComparisonPercentage
	]);

	if (loading) {
		return <ReturnCardListSkeleton />;
	}

	return (
		<div className={`w100 ${styles.didNotReturn}`}>
			<div className={`df aic ${compactForHeaderPopover ? "m-t-4" : "m-t-15"} ${styles.box}`}>
				{sortedChartData.map(renderChartItem)}
			</div>
			<NoRecordFound data={data} />
			<div
				className={compactForHeaderPopover ? "m-t-2 m-b-4" : "m-t-4 m-b-8"}
				style={{
					height: compactForHeaderPopover ? CHART_HEIGHT_HEADER : CHART_HEIGHT,
					width: "100%",
					maxWidth: compactForHeaderPopover ? 220 : "100%",
					marginLeft: compactForHeaderPopover ? "auto" : undefined,
					marginRight: compactForHeaderPopover ? "auto" : undefined,
					padding: "0px",
				}}
			>
				<ResponsivePie {...chartConfig} />
			</div>
		</div>
	);
};

export default ReturnCardList;
